import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { vendorTeamApi } from '@/services/vendor.api';
import { VendorRole, type VendorUserDTO } from '@pms/shared';

const errMsg = (e: unknown, fallback: string): string =>
  (e instanceof AxiosError &&
    (e.response?.data as { message?: string } | undefined)?.message) ||
  fallback;

/** Vendor team management (F5) — admin only; enforces the last-admin guard. */
export function VendorTeamPage() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<VendorRole>(VendorRole.OBSERVER);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ['vendor', 'team'],
    queryFn: vendorTeamApi.list,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ['vendor', 'team'] });

  const inviteMut = useMutation({
    mutationFn: () => vendorTeamApi.invite(email.trim(), role),
    onSuccess: (res) => {
      setError(null);
      setEmail('');
      setInviteLink(
        `${window.location.origin}/vendor/accept-invite?token=${res.token}`,
      );
      refresh();
    },
    onError: (e) => setError(errMsg(e, 'Invite failed')),
  });

  const toggleMut = useMutation({
    mutationFn: (m: VendorUserDTO) =>
      m.isActive ? vendorTeamApi.disable(m.id) : vendorTeamApi.enable(m.id),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e) => setError(errMsg(e, 'Update failed')),
  });

  const roleMut = useMutation({
    mutationFn: ({ id, r }: { id: string; r: VendorRole }) =>
      vendorTeamApi.setRole(id, r),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e) => setError(errMsg(e, 'Role change failed')),
  });

  return (
    <div className="max-w-4xl">
      <h1 className="mb-4 text-xl font-semibold text-white">Vendor team</h1>

      {error && (
        <div className="mb-4 rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="mb-6 rounded-lg border border-slate-800 bg-slate-900 p-4">
        <div className="mb-3 text-sm font-medium text-slate-300">Invite a member</div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="email"
            placeholder="email@innow8apps.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as VendorRole)}
            className="rounded-md border border-slate-700 bg-slate-800 px-2 py-2 text-sm text-white"
          >
            <option value={VendorRole.OBSERVER}>Observer</option>
            <option value={VendorRole.ADMIN}>Admin</option>
          </select>
          <button
            onClick={() => inviteMut.mutate()}
            disabled={inviteMut.isPending || !email.trim()}
            className="rounded-md bg-amber-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
          >
            Invite
          </button>
        </div>
        {inviteLink && (
          <div className="mt-3 break-all rounded-md bg-slate-800 px-3 py-2 text-xs text-emerald-300">
            Invite link (share once): {inviteLink}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="text-slate-400">Loading…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-900 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Last login</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-950">
              {members.map((m) => (
                <tr key={m.id}>
                  <td className="px-3 py-2 text-slate-200">{m.email}</td>
                  <td className="px-3 py-2">
                    <select
                      value={m.role}
                      onChange={(e) =>
                        roleMut.mutate({ id: m.id, r: e.target.value as VendorRole })
                      }
                      className="rounded border border-slate-700 bg-slate-800 px-1.5 py-1 text-xs text-white"
                    >
                      <option value={VendorRole.OBSERVER}>observer</option>
                      <option value={VendorRole.ADMIN}>admin</option>
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={
                        m.isActive ? 'text-emerald-400' : 'text-slate-500'
                      }
                    >
                      {m.isActive ? 'active' : 'disabled'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-500">
                    {m.lastLoginAt
                      ? new Date(m.lastLoginAt).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => toggleMut.mutate(m)}
                      className="rounded bg-slate-800 px-2 py-1 text-xs text-slate-200 hover:bg-slate-700"
                    >
                      {m.isActive ? 'Disable' : 'Enable'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
